import { makeAutoObservable, observable } from "mobx"
import { CreateProvisionEvent, EditProvisionEvent, ProvisionEvent, ProvisionEventEquipment } from "features/provisioning/provisions/models/provision-models"
import {
    deleteProvisionEvent,
    editProvisionEvent,
    getAllProvisionEvents,
    getProvisionEventsForAsset,
    postNewProvisionEvent,
} from "features/provisioning/provisions/services/provision-event-service"
import { StoreResultsGeneric } from "stores/stores-helper"

class ProvisionEventStore {
    provisionEventMap: Record<string, ProvisionEvent> = observable.object({})
    provisionEventLoading: boolean = false

    provisionEventsDetailMap: Record<string, ProvisionEventEquipment> = observable.object({})

    constructor() {
        makeAutoObservable(this)
    }

    get provisionEvents() {
        return Object.values(this.provisionEventMap)
    }

    get provisionEventsDetail() {
        return Object.values(this.provisionEventsDetailMap)
    }

    createProvisionEvent = async (event: CreateProvisionEvent, date: Date) => {
        const { provisionEventDetail } = await postNewProvisionEvent(event)
        this.provisionEventsDetailMap[provisionEventDetail.id] = observable.object(provisionEventDetail)
        return provisionEventDetail
    }

    loadProvisionEventsForAsset = async (assetId: string) => {
        const data = await getProvisionEventsForAsset(assetId)
        try {
            this.provisionEventsDetailMap = observable.object({})
            for (let provisionEvent of data) {
                this.provisionEventsDetailMap[provisionEvent.id] = observable.object(provisionEvent)
            }
        } catch (e) {
            console.log(e)
        }

        return this.provisionEventsDetail
    }

    loadAllProvisionEvents = async () => {
        if (this.provisionEventLoading) {
            return
        }
        this.provisionEventLoading = true

        const data = await getAllProvisionEvents()
        try {
            this.provisionEventMap = observable.object({})

            for (let provisionEvent of data) {
                this.provisionEventMap[provisionEvent.id] = observable.object(provisionEvent)
            }
            this.sortProvisionEventMap()
        } catch (e) {
            console.log(e)
        } finally {
            this.provisionEventLoading = false
        }

        return this.provisionEvents
    }

    saveProvisionEvent = async (
        entity: EditProvisionEvent,
    ): Promise<
        StoreResultsGeneric<{
            provisionEventDetail: ProvisionEventEquipment
        }>
    > => {
        if (this.provisionEventLoading) {
            return { passed: false, message: "", entityId: "" }
        }

        this.provisionEventLoading = true

        try {
            const response = await editProvisionEvent(entity)

            if (response.statusCode === 200) {
                const updatedEvents = await getProvisionEventsForAsset(entity.assetId)

                this.provisionEventsDetailMap = updatedEvents.reduce(
                    (acc, event) => observable.object({ ...acc, [event.id]: observable.object(event) }),
                    {},
                )
                this.sortProvisionEventMap()

                return {
                    passed: true,
                    message: "",
                    entityId: entity.id,
                    entities: { provisionEventDetail: this.provisionEventsDetailMap[entity.id] },
                }
            } else if (response.statusCode === 409) {
                return { passed: false, message: "Conflict-409: an entity with the same name is exist.", entityId: "" }
            } else {
                const message = "An error has occurred, please check the logs"
                return { passed: false, message, entityId: "" }
            }
        } catch (error) {
            console.error("Error creating entity:", error)
            return { passed: false, message: "An error has occurred, please check the logs", entityId: "" }
        } finally {
            this.provisionEventLoading = false
        }
    }

    private sortProvisionEventMap() {
        const sortedKeys = Object.keys(this.provisionEventMap).sort((a, b) => {
            return a < b ? -1 : 1
        })

        const sortedProvisionEventMap: Record<string, ProvisionEvent> = observable.object({})
        for (const key of sortedKeys) {
            sortedProvisionEventMap[key] = this.provisionEventMap[key]
        }

        this.provisionEventMap = sortedProvisionEventMap
    }

    delete = async (id: string) => {
        try {
            const response = await deleteProvisionEvent(id as string)
            if (response.statusCode === 200) {
                delete this.provisionEventMap[id]
                delete this.provisionEventsDetailMap[id]
                return { passed: true, message: "" }
            } else {
                const message = "An error has occurred, please check the logs"
                return { passed: false, message }
            }
        } catch (error) {
            console.error("Error creating customer:", error)
            return { passed: false, message: "An error has occurred, please check the logs" }
        } 
    }
}

export default new ProvisionEventStore()
